/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export const state = {
  vouchers: [],
  selectedVoucher: null,
  voucherServices: [],
  isLoading: false,
  isError: false,

  pagination: {
    page: 1,
    total: 0,
    next: null,
    page_size: 10,
  },

  filters: {
    active: null,
    q: null,
  },
};

export const getters = {
  formattedPagination: (state) => {
    const { page, total, next, page_size } = state.pagination;
    return {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(total / page_size)),
      nextPage: next,
      pageSize: page_size,
    };
  },
};

export const mutations = {
  SET_VOUCHERS(state, vouchers) { state.vouchers = vouchers; },
  SET_SELECTED_VOUCHER(state, voucher) { state.selectedVoucher = voucher; },
  SET_VOUCHER_SERVICES(state, services) { state.voucherServices = services; },
  SET_PAGINATION(state, pagination) { state.pagination = { ...state.pagination, ...pagination }; },
  SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters }; },
  SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
  SET_ERROR(state, isError) { state.isError = isError; },
};

export const actions = {
  async fetchVouchers({ commit, rootGetters, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', false);
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/vouchers`),
        {
          params: {
            page: state.pagination.page,
            page_size: state.pagination.page_size,
            active: state.filters.active,
            q: state.filters.q,
          },
        }
      );

      const { results, total, next } = res.data;
      commit('SET_VOUCHERS', results || []);
      commit('SET_PAGINATION', {
        total: total ?? state.pagination.total,
        next: next ?? state.pagination.next,
      });
      return results;
    } catch (error) {
      console.error('Fetch vouchers failed:', error);
      commit('SET_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createVoucher({ rootGetters }, formData) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/vouchers`),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    } catch (error) {
      console.error("Create voucher failed:", error);
      throw error.response?.data || error;
    }
  },

  async fetchVoucher({ commit, rootGetters }, voucherId) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!voucherId) throw new Error('Voucher ID is required');

    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/vouchers/${voucherId}`)
      );
      commit('SET_SELECTED_VOUCHER', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch voucher failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateVoucher({ rootGetters }, { voucherId, voucherData }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!voucherId) throw new Error("Voucher ID is required");

    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/vouchers/${voucherId}`),
        voucherData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    } catch (error) {
      console.error("Update voucher failed:", error);
      throw error.response?.data || error;
    }
  },

  async deleteVoucher({ rootGetters }, voucherId) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!voucherId) throw new Error('Voucher ID is required');

    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/vouchers/${voucherId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete voucher failed:', error);
      throw error.response?.data || error;
    }
  },

  async assignServicesToVoucher({ rootGetters }, { voucherId, services }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/vouchers/${voucherId}/services`),
        { service_ids: services }
      );
      return res.data;
    } catch (error) {
      console.error('Assign services failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchVoucherServices({ commit, rootGetters }, voucherId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/vouchers/${voucherId}/services`)
      );
      commit('SET_VOUCHER_SERVICES', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch voucher services failed:', error);
      throw error.response?.data || error;
    }
  },

  async replaceVoucherServices({ rootGetters }, { voucherId, services }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/vouchers/${voucherId}/services`),
        { service_ids: services }
      );
      return res.data;
    } catch (error) {
      console.error('Replace voucher services failed:', error);
      throw error.response?.data || error;
    }
  },

  async removeServiceFromVoucher({ rootGetters }, { voucherId, serviceId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/vouchers/${voucherId}/services/${serviceId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Remove service from voucher failed:', error);
      throw error.response?.data || error;
    }
  },

  updatePagination({ commit, dispatch, state }, newPagination = {}) {
    commit('SET_PAGINATION', { ...state.pagination, ...newPagination });
    return dispatch('fetchVouchers');
  },

  async updateFilters({ commit, dispatch }, newFilters) {
    commit('SET_FILTERS', newFilters);
    commit('SET_PAGINATION', { page: 1 });
    return dispatch('fetchVouchers');
  },

  async clearFilters({ commit, dispatch }) {
    commit('SET_FILTERS', { active: null, q: null });
    commit('SET_PAGINATION', { page: 1 });
    return dispatch('fetchVouchers');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
